import React, { useEffect, useMemo, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import PageContainer from '../../PageContainer';
import PartnerPlanCard from './PartnerPlanCard';
import { usePartnerPlansQuery } from '../../../api/use-partner-plans-query';
import { usePartnerPlansCheckoutMutation } from '../../../api/use-partner-plans-checkout-mutation';
import { usePortalTokens } from '../../../styles/usePortalTokens';

const PartnerManageSubscription = () => {
  const portal = usePortalTokens();
  const [searchParams, setSearchParams] = useSearchParams();
  const [checkoutPlanKey, setCheckoutPlanKey] = useState(null);

  const { data, isLoading, isError, error, refetch } = usePartnerPlansQuery();
  const checkoutMutation = usePartnerPlansCheckoutMutation();

  const plans = useMemo(() => data?.subscriptionPlans ?? [], [data?.subscriptionPlans]);

  const currentPlanKey = data?.currentPlanKey ?? null;

  useEffect(() => {
    const checkoutResult = searchParams.get('checkout');
    if (!checkoutResult) return;

    if (checkoutResult === 'success') {
      toast.success('Subscription updated successfully');
      refetch();
    } else if (checkoutResult === 'cancel') {
      toast('Checkout canceled');
    } else if (checkoutResult === 'failure') {
      toast.error('Checkout failed. Please try again.');
    }

    const next = new URLSearchParams(searchParams);
    next.delete('checkout');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, refetch]);

  const handleSelectPlan = async (plan) => {
    if (!plan?.key || checkoutMutation.isPending) return;

    setCheckoutPlanKey(plan.key);
    try {
      const result = await checkoutMutation.mutateAsync({ planKey: plan.key });
      window.location.assign(result.checkoutUrl);
    } catch (checkoutError) {
      toast.error(checkoutError?.message || 'Failed to start checkout');
      setCheckoutPlanKey(null);
    }
  };

  const columnCount = Math.min(Math.max(plans.length, 1), 3);

  return (
    <PageContainer
      title="Manage Subscription"
      contentSx={{
        bgcolor: portal.pageBg,
        backgroundImage: `linear-gradient(180deg, ${portal.pageBg} 0%, ${portal.surface} 100%)`,
      }}
    >
      <Box
        sx={{
          maxWidth: 1080,
          mx: 'auto',
          width: '100%',
          minWidth: 0,
          px: { xs: 0, sm: 0.5 },
        }}
      >
        <Box sx={{ textAlign: 'center', mb: { xs: 3, md: 4.5 }, px: { xs: 0.5, sm: 1 } }}>
          <Typography
            component="h1"
            sx={{
              fontWeight: 700,
              fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2.35rem' },
              color: 'text.primary',
              letterSpacing: '-0.02em',
              mb: 1,
              lineHeight: 1.25,
            }}
          >
            What kind of partner are you?
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{
              maxWidth: 520,
              mx: 'auto',
              lineHeight: 1.55,
              fontSize: { xs: '0.9375rem', md: '1rem' },
            }}
          >
            Pricing and event allowance are tailored to how you bring people together.
          </Typography>
        </Box>

        {isLoading ? (
          <Box display="flex" justifyContent="center" py={8}>
            <CircularProgress color="primary" />
          </Box>
        ) : null}

        {isError ? (
          <Typography color="error" variant="body2" textAlign="center">
            {error?.message || 'Failed to load subscription plans'}
          </Typography>
        ) : null}

        {!isLoading && !isError ? (
          plans.length === 0 ? (
            <Typography variant="body2" color="text.secondary" textAlign="center" py={6}>
              No subscription plans are available yet.
            </Typography>
          ) : (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: columnCount > 1 ? 'repeat(2, minmax(0, 1fr))' : '1fr',
                  lg: `repeat(${columnCount}, minmax(0, 1fr))`,
                },
                gap: { xs: 2, md: 2.5 },
                alignItems: 'stretch',
              }}
            >
              {plans.map((plan) => (
                <PartnerPlanCard
                  key={plan.pricingId || plan.planId || plan.key}
                  plan={plan}
                  isCurrent={Boolean(currentPlanKey && currentPlanKey === plan.key)}
                  isBusy={checkoutMutation.isPending && checkoutPlanKey === plan.key}
                  onSelect={handleSelectPlan}
                />
              ))}
            </Box>
          )
        ) : null}
      </Box>
    </PageContainer>
  );
};

export default PartnerManageSubscription;
